import React, { FocusEvent, KeyboardEvent, MouseEvent } from 'react'
import { isBefore } from 'date-fns'
import useClickOutside from '../../lib/hooks/useClickOutSide'
import useControlled from '../../lib/hooks/useControlled'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { createUseStyles } from '../../lib/styling'
import { getMinYear, getYearRange } from '../../lib/utils'
import Calendar, { CalendarProps, CalendarRef } from '../Calendar/Calendar'
import Inline from '../Inline'
import Popover from '../Popover'
import { CommonProps } from '../helperTypes'
import DateInput from './DateInput'

const useStyles = createUseStyles(
  () => ({
    popover: {
      '&[data-popper-placement*=bottom]': {
        clipPath: 'inset(2px -10px -10px -10px)',
      },
      '&[data-popper-placement*=top]': {
        clipPath: 'inset(-10px -10px 2px -10px)',
      },
    },
  }),
  'datepicker',
)

type Props = {
  /** customise the props passed to Calendar component */
  calendarProps?: CalendarProps
  /** Default selected date(s) */
  defaultValue?: Date | Date[] | null
  /**
   * Custom date input format
   * @default dd/mm/yyyy
   */
  dateFormat?: string
  /**
   * Custom date pattern
   * @default [0-9]{2}/[0-9]{2}/[0-9]{4}
   */
  datePattern?: string
  /**
   * date format & pattern separator
   * set this if you use a custom format
   * @default "/"
   */
  dateSeparator?: string
  /** disabled state */
  disabled?: boolean
  /**
   * First day of the week with possible values 0-6 (Sunday to Saturday).
   * @default 0
   */
  firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  /**
   * Custom formatter callback to customize conversion from calendar date to a string representation
   * Use together with formatStringTodate to customize date formats
   * @param date
   * @param separator
   * @param locale
   * @returns Date string representation
   */
  formatDateToString?: (date: Date, separator: string, locale: string) => string
  /**
   * Custom fromatter callback to customize conversion from input string to a Date object
   * Use together with formatDateToString to customize date formats
   * @param inputValue date input value
   * @param separator
   * @param locale
   * @returns Date instance
   */
  formatStringToDate?: (inputValue: string, separator: string, locale: string) => Date
  /**
   * Format the name of week day in calendar (short: one letter, long: three letters)
   * @default long
   */
  formatWeekDay?: 'short' | 'long'
  /** Props to pass to the input */
  inputProps?: any
  /** If true, the Datepicker will indicate an error */
  invalid?: boolean
  /** Defines whether the calendar should be opened */
  isOpen?: boolean
  /**
   * Defines which locale should be used by the date picker and the calendar
   * @default en-GB
   */
  locale?: string
  /** Defines maximum date that the user can select */
  maxDate?: Date
  /** Defines minimum date that the user can select */
  minDate?: Date
  /**
   * the number of month to display
   * @default 1
   */
  monthCount?: number
  /** Function called when the user change the date */
  onChange?: (date?: Date | Date[]) => void
  /** Function called when date format validation status is updated */
  onValidation?: (valid: boolean) => void
  /** Disable calendar date picking when set to true */
  simple?: boolean
  /** Defines whether the user shall select two dates forming a range instead of just one */
  range?: boolean
  /** Show the button clear in input box */
  showClearButton?: boolean
  /** Defines whether days from previous or next month shall be rendered if the month doesn't start on the first day of the week or doesn't end on the last day of the week, respectively */
  showOutsideDays?: boolean
  /** Defines the value of the calendar */
  value?: Date | Date[] | null
  /** If true, the Datepicker will indicate a success */
  valid?: boolean
  /**
   * Range of years allowed for selection.<br />
   * Ignored if both minDate and maxDate are provided.<br />
   * If only minDate or maxDate are provided the range will start from the one defined.
   * @default 100
   */
  yearRange?: number
}

type DeprecatedProps = {
  minView?: 'month' | 'year' | 'decade' | 'century'
  maxView?: 'month' | 'year' | 'decade' | 'century'
  onDateCallback?: (date: Date) => void
  showNeighboringMonth?: boolean
  showWeekNumbers?: boolean
  view?: 'month' | 'year' | 'decade' | 'century'
}

export type DatePickerProps = Props & DeprecatedProps & CommonProps

// check if date matched from input is in min/max range
const isOutOfRange = (date: Date, yearRangeProp: number, minDate?: Date, maxDate?: Date) => {
  const yearRange = getYearRange(yearRangeProp, minDate, maxDate)
  const minYear = getMinYear(yearRange, minDate, maxDate)
  if (minDate && isBefore(date, minDate)) return true
  if (maxDate && isBefore(maxDate, date)) return true
  if (date.getFullYear() < minYear) return true
  if (date.getFullYear() > minYear + yearRange) return true
  return false
}

// TODO separate text input into three inputs with separators (or use <input type="date"> on supported browsers)
// TODO implement showWeekNumbers ?
// TODO implement view / minView / maxView and navigation between them ?
// TODO manage start/end input focus in sync with date range calendar
// TODO invalid date input should trigger invalid state instead of resetting to "" ?
const DatePicker = (props: DatePickerProps) => {
  const {
    calendarProps,
    dateFormat = 'dd/mm/yyyy',
    datePattern = '[0-9]{2}/[0-9]{2}/[0-9]{4}',
    dateSeparator = '/',
    defaultValue,
    disabled = undefined,
    formatDateToString,
    formatStringToDate,
    inputProps,
    isOpen = false,
    locale = 'en-GB',
    minDate,
    maxDate,
    onChange,
    onValidation,
    range,
    simple,
    showClearButton,
    showNeighboringMonth,
    showOutsideDays = true,
    value: valueProp,
    invalid = false,
    valid = false,
    yearRange: yearRangeProp = 100,
    ...rest
  } = props
  useDeprecatedProps(
    'DatePicker',
    {
      minView: undefined,
      maxView: undefined,
      onDateCallback: undefined,
      showNeighboringMonth: 'showOutsideDays',
      showWeekNumbers: undefined,
      view: undefined,
    },
    props,
  )
  const classes = useStyles()

  const paperRef = React.useRef<HTMLDivElement>(null)
  const startInputRef = React.useRef<HTMLInputElement>(null)
  const endInputRef = React.useRef<HTMLInputElement>(null)
  const calendarRef = React.useRef<CalendarRef>(null)
  const startOpenBtnRef = React.useRef<HTMLButtonElement>(null)
  const endOpenBtnRef = React.useRef<HTMLButtonElement>(null)

  // support for controlled & uncontrolled API
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue: defaultValue,
  })
  const updateValue = (newValue?: Date | Date[]) => {
    setValue(newValue)
    onChange && onChange(newValue)
  }

  // validation state and callbacks
  const [startInputValue, setStartInputValue] = React.useState('')
  const [startInputInvalid, setStartInputInvalid] = React.useState(false)
  const [endInputValue, setEndInputValue] = React.useState('')
  const [endInputInvalid, setEndInputInvalid] = React.useState(false)
  const updateStartValidation = React.useCallback(
    (valid: boolean = false) => {
      setStartInputInvalid(!valid)
      if (onValidation) {
        range ? onValidation(valid && !endInputInvalid) : onValidation(valid)
      }
    },
    [endInputInvalid, onValidation, range],
  )
  const updateEndValidation = React.useCallback(
    (valid: boolean = false) => {
      setEndInputInvalid(!valid)
      if (onValidation && range) {
        onValidation(valid && !startInputInvalid)
      }
    },
    [onValidation, range, startInputInvalid],
  )

  // internal controlled values
  const intl = React.useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [locale],
  )
  const intlFormat = React.useMemo(() => {
    if (formatDateToString) {
      return (date: Date) => formatDateToString(date, dateSeparator, locale)
    }
    // default date to string implementation based on
    // Intl browser API
    return (date: Date) => {
      const parts = intl.formatToParts(date)
      return parts.reduce((acc, part) => {
        if (part.type === 'literal') return `${acc}${dateSeparator}`
        return `${acc}${part.value}`
      }, '')
    }
  }, [dateSeparator, formatDateToString, intl, locale])

  // reset input values when controlled value is cleared
  React.useEffect(() => {
    if (valueProp === null) {
      setStartInputValue('')
      setEndInputValue('')
      updateStartValidation(true)
      updateEndValidation(true)
    }
  }, [updateStartValidation, updateEndValidation, valueProp])
  // update inputs with valid date values
  // and automatically set focus to end input
  React.useEffect(() => {
    if (!value) return
    const startValue = range ? value[0] : value
    setStartInputValue(intlFormat(startValue))
    updateStartValidation(true)
    if (range) {
      // if (value.length === 1) endInputRef!.current!.focus();
      if (value.length === 2) {
        setEndInputValue(intlFormat(value[1]))
        updateEndValidation(true)
      }
    }
  }, [intlFormat, updateEndValidation, updateStartValidation, range, value])

  // calendar popover
  const [open, setOpen] = React.useState(isOpen)
  useClickOutside(paperRef!, (event) => {
    if (!open) return
    // exclude start/end inputs and calendar icon buttons
    if (
      event.target &&
      (event.target === startInputRef.current ||
        event.target === endInputRef.current ||
        event.target === startOpenBtnRef.current ||
        event.target === endOpenBtnRef.current)
    )
      return

    setOpen(false)
  })
  const handleInputClick = React.useCallback(
    (evt: MouseEvent<HTMLDivElement> | MouseEvent<HTMLButtonElement>) => {
      evt.preventDefault() // prevent useClickOutSide
      if (!open) setOpen(true)
    },
    [open],
  )
  const handleCalendarButtonClick = React.useCallback(
    (evt: MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation() // do not propagate to input
      setOpen(!open)
      if (!open && calendarRef.current) setTimeout(() => calendarRef.current!.focus())
    },
    [open],
  )

  // input focus management
  const setInputFocusOnClose = () => {
    // FIXME start or end input focus in range mode
    startInputRef.current!.focus()
    // focusing input will set calendar state to open
    // workaround is to ensure we asynchronously set calendar state to close
    setTimeout(() => setOpen(false))
  }

  // input change handlers
  const inputDateMatch = (dateStr: string) => {
    // here we simply check the format of the date
    // checks on min/max/range is done in handleStartInputChange/handleEndInputChange
    const match = dateStr.match(datePattern)
    if (match) {
      if (formatStringToDate) {
        return formatStringToDate(match[0], dateSeparator, locale)
      }
      const dateParts = match[0].split(dateSeparator)
      return new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]))
    }
    return false
  }
  const handleInputFocus = (evt: FocusEvent<HTMLInputElement>) => {
    const input = evt.target
    input.selectionStart = 0
    input.selectionEnd = input.value.length
    setOpen(true)
  }
  const handleInputBlur = (evt: FocusEvent<HTMLInputElement>) => {
    const dateStr = evt.target.value
    const dateMatch: Date | boolean = inputDateMatch(evt.target.value)
    if (evt.target === startInputRef.current) {
      updateStartValidation(!(dateStr !== '' && !dateMatch))
    } else {
      updateEndValidation(!(dateStr !== '' && !dateMatch))
    }
    // close calendar when moving focus out of inputs and calendar
    const isCalendarFocused = paperRef.current && paperRef.current.contains(evt.nativeEvent.relatedTarget as Node)
    if (isCalendarFocused) return
    if (evt.target === startInputRef.current || evt.target === endInputRef.current) {
      setOpen(false)
    }
  }
  const handleCalendarMouseDown = (evt: MouseEvent) => {
    // prevent onBlur on inputs
    evt.preventDefault()
  }
  const handleCalendarBlur = (evt: FocusEvent<HTMLDivElement>) => {
    const newFocusTarget = evt.nativeEvent.relatedTarget as Node
    const isCalendarFocused = paperRef.current && paperRef.current.contains(newFocusTarget)
    if (isCalendarFocused) return
    if (newFocusTarget !== startInputRef.current && endInputRef.current && newFocusTarget !== endInputRef.current) {
      setOpen(false)
    }
  }
  const handleStartInputChange = (dateStr: string) => {
    const dateMatch: Date | boolean = inputDateMatch(dateStr)
    if (dateMatch && dateMatch.toString() !== 'Invalid Date') {
      const outOfRange = isOutOfRange(dateMatch, yearRangeProp, minDate, maxDate)

      // date range mode
      if (range) {
        // reset start date if
        // - after end date
        // - out of min/max range
        if ((value && value[1] && isBefore(value[1], dateMatch)) || outOfRange) {
          setStartInputValue(value && value[0] ? intlFormat(value[0]) : '')
        } else {
          endInputRef!.current!.focus()
          updateValue(value && value.length === 2 ? [dateMatch, value[1]] : [dateMatch])
        }
      } else {
        if (outOfRange) {
          updateValue(undefined)
          setStartInputValue('')
        } else {
          updateValue(dateMatch)
        }
      }
    } else if (dateStr === '') {
      // reset if input has been cleared
      updateValue(undefined)
      setStartInputValue('')
      setEndInputValue('')
      updateStartValidation(true)
      updateEndValidation(true)
    } else {
      // simply update input state
      setStartInputValue(dateStr)
    }
  }
  const handleEndInputChange = (dateStr: string) => {
    const dateMatch: Date | boolean = inputDateMatch(dateStr)
    if (dateMatch && dateMatch.toString() !== 'Invalid Date') {
      // reset end date if
      // - before start date
      // - out of min/max range
      if (isBefore(dateMatch, value && value[0]) || isOutOfRange(dateMatch, yearRangeProp, minDate, maxDate)) {
        setEndInputValue(value && value[1] ? intlFormat(value[1]) : '')
      } else {
        updateValue([value ? value[0] : undefined, dateMatch])
      }
    } else if (dateStr === '') {
      // reset if input has been cleared
      updateValue([value ? value[0] : undefined])
      setEndInputValue('')
    } else {
      // simply update input state
      setEndInputValue(dateStr)
    }
  }
  const handleCalendarChange = (newValue: Date | Date[]) => {
    // reset last date in range mode when clicking a new date in calendar
    if (range && Array.isArray(newValue) && newValue.length === 1) {
      setEndInputValue('')
    }
    updateValue(newValue)
    if (!range || (Array.isArray(newValue) && newValue.length === 2)) {
      setInputFocusOnClose()
    }
  }

  // keyboard navigation
  const handleInputKeyDown = (evt: KeyboardEvent) => {
    const managedKeys = ['ArrowDown', 'ArrowUp', 'Escape']
    if (!managedKeys.includes(evt.key)) return

    // Close calendar
    if (open && evt.key === 'Escape') {
      evt.stopPropagation()
      setOpen(false)
    }
    // ArrowDown key to open calendar / switch focus to active day
    else if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
      if (!open) {
        setOpen(true)
      } else {
        calendarRef.current!.focus()
      }
      evt.preventDefault()
    }
  }
  const handleCalendarKeyDown = (evt: KeyboardEvent) => {
    const managedKeys = ['Escape']
    if (!open || !managedKeys.includes(evt.key)) return

    // calendar displaying a different month than active one
    // move calendar to active month and focus on active day
    if (calendarRef.current!.getOffset() !== 0) {
      calendarRef.current!.setOffset(0)
      setTimeout(() => calendarRef.current!.focus())
    } else {
      evt.stopPropagation()
      setInputFocusOnClose()
    }
  }

  const inputCommonProps = {
    ...inputProps,
    disabled,
    onBlur: handleInputBlur,
    onClick: handleInputClick,
    onCalendarClick: handleCalendarButtonClick,
    onFocus: handleInputFocus,
    onKeyDown: handleInputKeyDown,
    placeholder: dateFormat,
    pattern: datePattern,
    separator: dateSeparator,
    showCalendarButton: !simple,
    showClearButton: showClearButton,
  }
  return (
    <>
      {range ? (
        <Inline spacing='2-x'>
          <DateInput
            {...inputCommonProps}
            invalid={invalid || startInputInvalid}
            onChange={handleStartInputChange}
            inputRef={startInputRef}
            openBtnRef={startOpenBtnRef}
            value={startInputValue}
            valid={valid}
          />
          <DateInput
            {...inputCommonProps}
            invalid={invalid || endInputInvalid}
            onChange={handleEndInputChange}
            inputRef={endInputRef}
            openBtnRef={endOpenBtnRef}
            value={endInputValue}
            valid={valid}
          />
        </Inline>
      ) : (
        <DateInput
          {...inputCommonProps}
          invalid={invalid || startInputInvalid}
          onChange={handleStartInputChange}
          inputRef={startInputRef}
          openBtnRef={startOpenBtnRef}
          value={startInputValue}
          valid={valid}
        />
      )}
      <Popover
        arrow={false}
        offset={[0, 0]}
        open={!simple && open}
        placement='bottom-start'
        fallbackPlacements={['top-start']}
        referenceNode={startInputRef!.current}
        ref={paperRef}
        className={classes.popover}
      >
        <Calendar
          {...rest}
          {...calendarProps}
          ref={calendarRef}
          locale={locale}
          minDate={minDate}
          maxDate={maxDate}
          yearRange={yearRangeProp}
          mode={range ? 'range' : 'single'}
          onBlurCapture={handleCalendarBlur}
          onChange={handleCalendarChange}
          onMouseDown={handleCalendarMouseDown}
          onKeyDown={handleCalendarKeyDown}
          showOutsideDays={showNeighboringMonth || showOutsideDays}
          value={value || ''}
        />
      </Popover>
    </>
  )
}

export default DatePicker
